import { Component, createRef, lazy, Suspense, useEffect, useRef, type ErrorInfo, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { TooltipProvider } from "@pinpoint/ui/tooltip";
import PublicLayout from "./layouts/PublicLayout";
import PublicHome from "./pages/PublicHome";
import { HostTransfer } from "./routes/HostTransfer";
import { MarketingAnalyticsOwner } from "./analytics/marketingAnalyticsRuntime";
import type { MarketingReporter } from "./analytics/marketingEvents";
import "./styles/tailwind-base.css";
import "@pinpoint/brand/tokens.css";
import "./styles/marketing-fonts.css";
import "./styles/index.css";

const DecisionSystemPage = lazy(() => import("./pages/DecisionSystemPage"));
const DemoPageV2 = lazy(() => import("./pages/DemoPageV2"));
const DemoUIPage = lazy(() => import("./pages/DemoUIPage"));
const IntroductionPage = lazy(() => import("./pages/IntroductionPage"));
const SatRwDistributionPage = lazy(() => import("./pages/sat/SatRwDistributionPage"));
const LearningGPSShell = lazy(() => import("./features/kg/components/layout/LearningGPSShell")
  .then(({ LearningGPSShell: component }) => ({ default: component })));
const PlanPage = lazy(() => import("./features/kg/pages/PlanPage")
  .then(({ PlanPage: component }) => ({ default: component })));
const PracticePage = lazy(() => import("./features/kg/pages/PracticePage")
  .then(({ PracticePage: component }) => ({ default: component })));
const ProgressPage = lazy(() => import("./features/kg/pages/ProgressPage")
  .then(({ ProgressPage: component }) => ({ default: component })));
const ExplorePage = lazy(() => import("./features/kg/pages/ExplorePage")
  .then(({ ExplorePage: component }) => ({ default: component })));

type Seo = { title: string; description: string; indexable: boolean };

type MarketingRouteErrorBoundaryProps = { children: ReactNode; onRetry?: () => void };
type MarketingRouteErrorBoundaryState = { failed: boolean };

export class MarketingRouteErrorBoundary extends Component<
  MarketingRouteErrorBoundaryProps,
  MarketingRouteErrorBoundaryState
> {
  state = { failed: false };
  private heading = createRef<HTMLHeadingElement>();

  static getDerivedStateFromError(): MarketingRouteErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Marketing route failed to load", error, info.componentStack);
    this.heading.current?.focus();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const retry = this.props.onRetry ?? (() => window.location.reload());
    return (
      <main className="flex min-h-screen items-center justify-center px-6 text-center">
        <div>
          <h1 ref={this.heading} tabIndex={-1} className="text-4xl font-bold">This page could not load</h1>
          <p className="mt-4 text-muted-foreground">Reload to get the latest version of the public site.</p>
          <button type="button" onClick={retry} className="mt-8 rounded bg-primary px-5 py-3 font-semibold text-primary-foreground">
            Reload page
          </button>
        </div>
      </main>
    );
  }
}

export function isSyntheticMarketingPath(pathname: string) {
  return pathname === "/sat/kg-mock" || pathname.startsWith("/sat/kg-mock/");
}

const seoByPath: Record<string, Seo> = {
  "/": {
    title: "Pinpoint Edu | Decision Intelligence for Learning",
    description: "Decision intelligence that helps every learner focus on the next skill that matters most.",
    indexable: true,
  },
  "/demo": {
    title: "Pinpoint Edu Learning Demo",
    description: "See how Pinpoint Edu turns a diagnostic into an actionable learning plan.",
    indexable: true,
  },
  "/demo_ui": {
    title: "Pinpoint Edu System Demo",
    description: "Explore the Pinpoint Edu learning decision system through a guided demonstration.",
    indexable: true,
  },
  "/decision-system": {
    title: "How Pinpoint Edu Makes Learning Decisions",
    description: "Understand how Pinpoint Edu identifies the learning decision that matters most next.",
    indexable: true,
  },
  "/introduction": {
    title: "Introducing Pinpoint Edu",
    description: "Meet Pinpoint Edu and its focused approach to zero-waste learning.",
    indexable: true,
  },
  "/sat/info/reading-writing-distribution": {
    title: "SAT Reading and Writing Distribution | Pinpoint Edu",
    description: "Review the SAT Reading and Writing domain and skill distribution.",
    indexable: true,
  },
};

function setMeta(selector: string, attributes: Record<string, string>) {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.append(node);
  }
  for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, value);
}

function PageMetadata({ path, children }: { path: string; children: ReactNode }) {
  useEffect(() => {
    const synthetic = isSyntheticMarketingPath(path);
    const seo = synthetic ? {
      title: "Synthetic Learning GPS Demo | Pinpoint Edu",
      description: "Explore the Learning GPS with synthetic demonstration data.",
      indexable: false,
    } : seoByPath[path];
    if (!seo) return;
    const canonical = `https://pinpointedu.com${path}`;
    document.title = seo.title;
    setMeta('meta[name="description"]', { name: "description", content: seo.description });
    setMeta('meta[name="robots"]', { name: "robots", content: seo.indexable ? "index, follow" : "noindex, nofollow" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: seo.title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: seo.description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.append(link);
    }
    link.href = canonical;
  }, [path]);
  return children;
}

function SyntheticLearningGps() {
  return (
    <PageMetadata path={window.location.pathname}>
      <div className="relative">
        <p className="absolute left-1/2 top-2 z-30 -translate-x-1/2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
          Synthetic demonstration data
        </p>
        <Suspense fallback={<main aria-busy="true" className="min-h-screen" />}>
          <LearningGPSShell />
        </Suspense>
      </div>
    </PageMetadata>
  );
}

function NotFound() {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    document.title = "Page Not Found | Pinpoint Edu";
    document.head.querySelectorAll(
      'meta[name="description"], meta[property^="og:"], meta[name="twitter:card"], link[rel="canonical"]',
    ).forEach((node) => node.remove());
    setMeta('meta[name="robots"]', { name: "robots", content: "noindex, nofollow" });
    heading.current?.focus();
  }, []);
  return (
    <main className="flex min-h-screen items-center justify-center px-6 text-center">
      <div>
        <h1 ref={heading} tabIndex={-1} className="text-4xl font-bold">Page not found</h1>
        <p className="mt-4 text-muted-foreground">The page you requested is not part of the Pinpoint Edu public site.</p>
        <div className="mt-8 flex justify-center gap-4">
          <a href="/" className="text-primary underline">Home</a>
          <a href="/demo" className="text-primary underline">Demo</a>
        </div>
      </div>
    </main>
  );
}

function IndexedPage({ path, children }: { path: string; children: ReactNode }) {
  return <PageMetadata path={path}>{children}</PageMetadata>;
}

function CompatibilityRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

export function MarketingRouteRoot() {
  return (
    <MotionConfig reducedMotion="user">
      <TooltipProvider>
        <MarketingRouteErrorBoundary>
        <Suspense fallback={<main aria-busy="true" className="min-h-screen" />}>
        <Routes>
        <Route path="/" element={<IndexedPage path="/"><PublicLayout><PublicHome /></PublicLayout></IndexedPage>} />
        <Route path="/demo" element={<IndexedPage path="/demo"><DemoPageV2 /></IndexedPage>} />
        <Route path="/demo_ui" element={<IndexedPage path="/demo_ui"><DemoUIPage /></IndexedPage>} />
        <Route path="/decision-system" element={<IndexedPage path="/decision-system"><DecisionSystemPage /></IndexedPage>} />
        <Route path="/introduction" element={<IndexedPage path="/introduction"><IntroductionPage /></IndexedPage>} />
        <Route path="/sat/info/reading-writing-distribution" element={<IndexedPage path="/sat/info/reading-writing-distribution"><SatRwDistributionPage /></IndexedPage>} />
        <Route path="/next/app/*" element={<HostTransfer path="/next/app/*" destinationHost="app" />} />
        <Route path="/next/login" element={<HostTransfer path="/next/login" destinationHost="app" />} />
        <Route path="/deck" element={<CompatibilityRedirect to="/deck.html" />} />
        <Route path="/deck/preseed" element={<CompatibilityRedirect to="/preseed_deck.html" />} />
        <Route path="/hangman" element={<CompatibilityRedirect to="/hangman.html" />} />
        <Route path="/sat/kg-mock" element={<SyntheticLearningGps />}>
          <Route index element={<Navigate to="plan" replace />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="practice" element={<PracticePage />} />
          <Route path="progress" element={<ProgressPage />} />
          <Route path="explore" element={<ExplorePage />} />
        </Route>
        <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </MarketingRouteErrorBoundary>
      </TooltipProvider>
    </MotionConfig>
  );
}

export function MarketingApp({ report }: { report?: MarketingReporter } = {}) {
  return <BrowserRouter><MarketingAnalyticsOwner report={report}><MarketingRouteRoot /></MarketingAnalyticsOwner></BrowserRouter>;
}

export default MarketingApp;
